import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Printer, CheckSquare, Square } from "lucide-react";

/**
 * @typedef {Object} PrintDocumentItem
 * @property {string} id — Passed back in onPrint for routing / API keys
 * @property {string} name — Row label (DOCUMENT NAME)
 * @property {string} [remarks] — Shown in green (e.g. "Arrival Notice Count : 1")
 * @property {number} [defaultCopies]
 * @property {boolean} [defaultSelected]
 * @property {boolean} [highlightRow] — Yellow row background (e.g. Rate Quote)
 */

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {PrintDocumentItem[]} props.documents
 * @param {(payload: { items: { id: string, name: string, copies: number }[], blPrintOnLetterhead: boolean }) => void | Promise<void>} [props.onPrint] — Implement PDF / print here (API, window.open, jsPDF, etc.)
 * @param {string} [props.title]
 */
export default function PrintDocumentsSelectionModal({
  isOpen,
  onClose,
  documents = [],
  onPrint,
  title = "Print Documents",
}) {
  const docKey = useMemo(() => JSON.stringify(documents), [documents]);

  const [rows, setRows] = useState([]);
  const [blPrintOnLetterhead, setBlPrintOnLetterhead] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setRows(
      documents.map((d) => ({
        id: d.id,
        name: d.name,
        remarks: d.remarks ?? "",
        copies: Math.max(1, Number(d.defaultCopies) || 1),
        selected: Boolean(d.defaultSelected),
        highlightRow: Boolean(d.highlightRow),
      }))
    );
    setBlPrintOnLetterhead(false);
  }, [isOpen, docKey]);

  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  const handlePrint = async () => {
    const selected = rows.filter((r) => r.selected);
    if (!selected.length) {
      toast.error("Please select at least one document.");
      return;
    }

    const payload = {
      items: selected.map((r) => ({
        id: r.id,
        name: r.name,
        copies: r.copies,
      })),
      blPrintOnLetterhead: blPrintOnLetterhead,
    };

    if (typeof onPrint === "function") {
      try {
        await onPrint(payload);
      } catch (e) {
        console.error(e);
        toast.error("Print / PDF failed. Check console or try again.");
      }
    } else {
      toast.message("Add onPrint handler", {
        description:
          "Wire onPrint on the parent to generate PDFs from the selected items.",
      });
      console.info("PrintDocumentsSelectionModal payload:", payload);
    }
  };

  const toggleAll = () => {
    const allSelected = rows.every((r) => r.selected);
    setRows((prev) => prev.map((r) => ({ ...r, selected: !allSelected })));
  };

  const selectCount = rows.filter((r) => r.selected).length;
  const totalCount = rows.length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm transition-all duration-200">
      <div
        className="relative mx-4 w-full max-w-4xl transform overflow-hidden rounded-2xl bg-white shadow-2xl transition-all duration-200 dark:bg-gray-900"
        role="dialog"
        aria-modal="true"
        aria-labelledby="print-documents-title"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 dark:border-gray-700 dark:from-blue-700 dark:to-blue-800">
          <div>
            <h2
              id="print-documents-title"
              className="text-xl font-semibold text-white"
            >
              {title}
            </h2>
            <p className="mt-1 text-sm text-blue-100">
              Select documents and specify number of copies
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-white/80 transition-all hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-white/20"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Table */}
        <div className="max-h-[480px] overflow-y-auto">
          <table className="w-full border-collapse">
            <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
              <tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <th className="px-6 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={toggleAll}
                      className="flex items-center gap-1 text-xs font-medium hover:text-gray-700 dark:hover:text-gray-300"
                    >
                      {selectCount === totalCount && totalCount > 0 ? (
                        <CheckSquare className="h-4 w-4" />
                      ) : (
                        <Square className="h-4 w-4" />
                      )}
                      <span>DOCUMENT NAME</span>
                    </button>
                  </div>
                </th>
                <th className="px-6 py-3">REMARKS</th>
                <th className="w-28 px-6 py-3 text-center"># COPIES</th>
                <th className="w-20 px-6 py-3 text-center">SELECT</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.map((row, index) => (
                <tr
                  key={row.id}
                  className={`transition-colors duration-150 ${row.highlightRow
                    ? "bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/50"
                    : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800"
                    }`}
                >
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {row.name}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <span className="text-sm text-emerald-600 dark:text-emerald-400">
                      {row.remarks || "—"}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-center">
                    <input
                      type="number"
                      min={1}
                      value={row.copies}
                      onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        updateRow(index, {
                          copies: Number.isFinite(n) && n >= 1 ? n : 1,
                        });
                      }}
                      disabled={!row.selected}
                      className="w-20 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-center text-sm transition-all focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-blue-400 dark:disabled:bg-gray-700"
                    />
                  </td>
                  <td className="px-6 py-3 text-center">
                    <input
                      type="checkbox"
                      checked={row.selected}
                      onChange={(e) =>
                        updateRow(index, { selected: e.target.checked })
                      }
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-0 dark:border-gray-600 dark:bg-gray-800 dark:checked:bg-blue-600"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-800/50">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-red-600 dark:text-red-400">
              <input
                type="checkbox"
                checked={blPrintOnLetterhead}
                onChange={(e) => setBlPrintOnLetterhead(e.target.checked)}
                className="h-4 w-4 rounded border-red-300 text-red-600 focus:ring-2 focus:ring-red-500/20 dark:border-red-700 dark:bg-gray-800"
              />
              <span>BL Print on Letterhead</span>
            </label>

            <div className="flex items-center justify-end gap-3">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {selectCount} of {totalCount} selected
              </span>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-5 py-2 text-sm font-medium text-gray-700 transition-all hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePrint}
                className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-green-600 to-green-700 px-6 py-2 text-sm font-medium text-white transition-all hover:from-green-700 hover:to-green-800 focus:outline-none focus:ring-2 focus:ring-green-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Printer className="h-4 w-4" />
                Print ({selectCount})
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}